/**
 * Thin client for systemapi DICOM/PACS routes used by EEG–imaging co-review.
 */

import apiClient from "@shared/services/apiClient";
import type { ImagingStudyRef } from "../utils/eegImagingLinkage";

export type PacsQueryStudy = {
  studyInstanceUID?: string;
  seriesInstanceUID?: string;
  modality?: string;
  studyDescription?: string;
  patientID?: string;
  patientName?: string;
  studyDate?: string;
  accessionNumber?: string;
};

export type PacsQueryResponse = {
  success: boolean;
  data?: {
    patientId: string;
    studyCount: number;
    studies: PacsQueryStudy[];
  };
  error?: string;
  details?: string;
};

export type PacsRetrieveResponse = {
  success: boolean;
  data?: {
    studyInstanceUID: string;
    fileCount: number;
    files?: string[];
    outputDir?: string;
  };
  error?: string;
  details?: string;
};

export function mapPacsStudiesToImagingRefs(
  studies: PacsQueryStudy[],
  patientId: string,
): ImagingStudyRef[] {
  return studies
    .filter((s) => Boolean(s.studyInstanceUID))
    .map((s) => {
      const studyInstanceUID = String(s.studyInstanceUID);
      return {
        studyInstanceUID,
        seriesInstanceUID: s.seriesInstanceUID || null,
        modality: s.modality || null,
        description: s.studyDescription || null,
        source: "pacs" as const,
        patientId: s.patientID || patientId,
        accessionNumber: s.accessionNumber || null,
        viewerUrl: `/imaging/viewer?studyUID=${encodeURIComponent(studyInstanceUID)}`,
      };
    });
}

export async function queryPacsForPatient(
  patientId: string,
): Promise<{ studies: PacsQueryStudy[]; error: string | null }> {
  try {
    const res = await apiClient.get<PacsQueryResponse>(
      `/dicom/query/${encodeURIComponent(patientId)}`,
    );
    const body = res.data;
    if (!body?.success) {
      return {
        studies: [],
        error: body?.details || body?.error || "PACS query failed",
      };
    }
    return { studies: body.data?.studies || [], error: null };
  } catch (err: unknown) {
    const message =
      err && typeof err === "object" && "response" in err
        ? String(
            (err as { response?: { data?: { details?: string; error?: string } } })
              .response?.data?.details ||
              (err as { response?: { data?: { error?: string } } }).response?.data
                ?.error ||
              (err instanceof Error ? err.message : undefined) ||
              "PACS query failed",
          )
        : err instanceof Error
          ? err.message
          : "PACS query failed";
    return { studies: [], error: message };
  }
}

export async function retrievePacsStudy(
  studyInstanceUID: string,
): Promise<{ ok: boolean; fileCount: number; error: string | null }> {
  try {
    const res = await apiClient.post<PacsRetrieveResponse>("/dicom/retrieve", {
      studyInstanceUID,
    });
    const body = res.data;
    if (!body?.success) {
      return {
        ok: false,
        fileCount: 0,
        error: body?.details || body?.error || "PACS retrieve failed",
      };
    }
    return {
      ok: true,
      fileCount: body.data?.fileCount ?? body.data?.files?.length ?? 0,
      error: null,
    };
  } catch (err: unknown) {
    const message =
      err instanceof Error ? err.message : "PACS retrieve failed";
    return { ok: false, fileCount: 0, error: message };
  }
}
